/**
 * Pure, framework-free types + helpers for the Copilot message-forwarding
 * feature. Kept separate from the React context so the consume-once / target-
 * match logic is trivially unit-testable (the dashboard has no jest runner yet,
 * but this module imports nothing from React/DOM so a test can be added the
 * moment one exists — see note in the feature report).
 */

/** Where a forwarded message is destined. */
export type ForwardTargetKind = 'copilot' | 'chat' | 'ticket'

export interface ForwardTarget {
  kind: ForwardTargetKind
  /** Session id for copilot ('new' to create a fresh one), or chat/ticket id. */
  id?: string | number
}

/** A piece of text queued to prefill a composer once the agent lands on it. */
export interface PendingForward {
  text: string
  target: ForwardTarget
}

/** The chat/ticket an agent came FROM, so the answer can be sent back. */
export interface ReturnTo {
  kind: 'chat' | 'ticket'
  id: string | number
  label: string
  path: string
}

/**
 * Decide whether a pending forward should be consumed by the caller that owns
 * `matcher`. Returns the text to prefill (and signals the pending state should
 * be cleared) only when the matcher accepts the pending target; otherwise null.
 *
 * This is the heart of "consume-once + target-match": each composer passes a
 * matcher describing the target it represents, so a pending forward is taken by
 * exactly the intended composer, exactly once.
 */
export function matchPending(
  pending: PendingForward | null,
  matcher: (target: ForwardTarget) => boolean
): string | null {
  if (!pending) return null
  if (!matcher(pending.target)) return null
  return pending.text
}

/** Loose id equality that tolerates number vs string ids from the API. */
export function sameId(a: string | number | undefined, b: string | number | undefined): boolean {
  if (a == null || b == null) return false
  return String(a) === String(b)
}
